import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileDigit, ArrowRight, ClipboardList, BadgeCheck, Network, FileSearch, GraduationCap, RefreshCw,
  CheckCircle, HelpCircle, Phone, Mail, MapPin, Calculator, Users, Settings2, LifeBuoy, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import RelatedServices from '../components/RelatedServices';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';

const BOOK_URL = '/contact?service=e-invoicing&form=consultation';

const ROLES = [
  ['Readiness assessment and implementation planning', 'Technical configuration and connectivity'],
  ['Invoice data and VAT classification review', 'Invoice validation, conversion and exchange'],
  ['Provider selection and onboarding support', 'Tax data reporting through the required channels'],
  ['Testing coordination and staff training', 'Technical monitoring and troubleshooting'],
  ['Reconciliation and ongoing tax support', 'Platform operation and maintenance']
];

const SERVICES = [
  { icon: ClipboardList, title: 'Readiness Assessment & Gap Analysis', lead: 'Understand what your business needs to change before implementation.', items: [
    'Review existing invoicing, purchasing and accounting workflows.',
    'Assess your accounting software or ERP’s integration options.',
    'Identify gaps in customer, supplier and invoice data.',
    'Review invoice approval processes and internal controls.',
    'Prepare a practical implementation roadmap with priorities and responsibilities.'
  ] },
  { icon: BadgeCheck, title: 'Accredited Service Provider Selection & Onboarding', lead: 'Choose a provider suited to your business requirements.', items: [
    'Assess transaction volumes, operational needs and existing systems.',
    'Support comparison of provider functionality, integration options and costs.',
    'Check the selected provider’s status against the official MoF list.',
    'Coordinate onboarding and communication with the provider.',
    'Help define responsibilities, support arrangements and escalation procedures.'
  ] },
  { icon: Network, title: 'ERP & Accounting System Integration Coordination', lead: 'Coordinate the connection between your accounting system and the selected ASP.', items: [
    'Review available connectors, APIs and file-based exchange options.',
    'Coordinate field mapping with your software and technology providers.',
    'Support testing of sales invoices, supplier invoices and credit notes.',
    'Track validation issues and coordinate corrective action.',
    'Support go-live planning and initial operational checks.'
  ], note: 'Integration options depend on your software version, configuration and the selected provider’s capabilities.' },
  { icon: FileSearch, title: 'Invoice Data Mapping & VAT Review', lead: 'Help ensure your invoice data reflects the underlying transactions correctly.', items: [
    'Review required customer, supplier and invoice fields.',
    'Check VAT categories, tax codes and calculation logic.',
    'Review the treatment of zero-rated, exempt and reverse charge transactions, where applicable.',
    'Support mapping of credit notes, discounts, adjustments and cross-border transactions.',
    'Review alignment with applicable UAE e-invoicing data requirements, including PINT AE.'
  ] },
  { icon: GraduationCap, title: 'Staff Training & Process Documentation', lead: 'Prepare your people for the new workflow.', items: [
    'Train sales, billing, purchasing and accounting teams.',
    'Document invoice preparation, review and approval procedures.',
    'Establish processes for handling rejected invoices and other exceptions.',
    'Define responsibilities for corrections and follow-up.',
    'Guide staff on maintaining supporting records and audit trails.'
  ] },
  { icon: RefreshCw, title: 'Ongoing Compliance & Reconciliation Support', lead: 'Maintain oversight after implementation.', items: [
    'Review transaction status reports and unresolved exceptions.',
    'Reconcile e-invoice records with accounting ledgers.',
    'Support reconciliation with periodic VAT returns.',
    'Review recurring data issues and recommend improvements.',
    'Coordinate with the ASP on technical matters within the agreed support scope.'
  ] }
];

const WHY = [
  { icon: Calculator, title: 'Accounting and Tax Expertise', text: 'We review the accounting and VAT treatment behind your invoice data, helping your team identify issues before they become recurring operational problems.' },
  { icon: Users, title: 'One Point of Coordination', text: 'We help coordinate your finance team, software vendor and selected ASP, with clear responsibilities throughout implementation.' },
  { icon: Settings2, title: 'Support Tailored to Your Business', text: 'Our approach considers your business size, invoice volumes, existing systems and internal resources.' },
  { icon: LifeBuoy, title: 'Continued Support After Go-Live', text: 'Our services can extend beyond implementation to include reconciliations, exception reviews and ongoing accounting and tax support.' }
];

const FAQS = [
  { q: 'Is WPAA an Accredited Service Provider?', a: 'No. WPAA provides audit, accounting, tax advisory and implementation coordination services. Technical invoice exchange and tax data reporting are delivered by the appointed accredited service provider. WPAA’s role focuses on readiness, data review, coordination, training and ongoing accounting and tax support.' },
  { q: 'Will we need to replace our accounting software?', a: 'Not necessarily. We assess whether your existing software can connect to a suitable ASP through available connectors, APIs or other supported methods. Any required upgrade or replacement depends on the system’s capabilities and your business requirements.' },
  { q: 'Can you support Zoho Books, QuickBooks, Xero, Tally, SAP or Oracle?', a: 'We can assess your requirements and coordinate with the relevant software vendor and selected ASP. Compatibility and implementation scope are confirmed after reviewing your software version, configuration and available integration options.' },
  { q: 'Do PDF invoices qualify as e-invoices?', a: 'A PDF alone is not a structured e-invoice under the UAE framework. A human-readable copy may still form part of your business workflow, alongside the required structured electronic exchange.' },
  { q: 'When should we start preparing?', a: 'Start by assessing your applicable implementation requirements and the work needed to prepare your data, systems and staff. Early planning allows time for provider selection, configuration, testing and training.' },
  { q: 'How much does implementation support cost?', a: 'Fees depend on your systems, transaction volumes, data readiness, integration complexity and support requirements. WPAA will provide a proposal defining our professional fees and scope. ASP subscriptions, software upgrades and technical implementation charges will be identified separately where applicable.' }
];

const BookButton = ({ testId, light }) => (
  <Link to={BOOK_URL} data-testid={testId}>
    <Button size="lg" className={`${light ? 'bg-white text-indigo-700 hover:bg-gray-100' : 'bg-indigo-500 hover:bg-indigo-600 text-white'} h-auto whitespace-normal px-6 sm:px-8 py-4 text-base sm:text-lg`}>
      Book an E-Invoicing Readiness Consultation
      <ArrowRight className="ml-2" size={20} />
    </Button>
  </Link>
);

const Eyebrow = ({ children }) => <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">{children}</span>;

const Hero = () => (
  <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-indigo-900 via-indigo-800 to-gray-900 text-white overflow-hidden">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-400 rounded-full filter blur-3xl" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full filter blur-3xl" />
    </div>
    <div className="container mx-auto relative z-10">
      <div className="max-w-4xl">
        <div className="flex items-center space-x-2 mb-6">
          <FileDigit className="text-indigo-300" size={32} />
          <span className="text-indigo-300 font-semibold text-sm uppercase tracking-wider">Tax Services · E-Invoicing</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">E-Invoicing Compliance &amp; Integration Services in the UAE</h1>
        <p className="text-xl md:text-2xl text-indigo-200 mb-6 font-semibold">Tax Expertise. Practical Preparation. Coordinated Implementation.</p>
        <p className="text-lg text-gray-300 mb-4 leading-relaxed max-w-3xl">Prepare your business for UAE e-invoicing with White Paper Accounts Auditing (WPAA).</p>
        <p className="text-lg text-gray-400 mb-8 max-w-3xl">
          We help businesses assess their readiness, improve invoice data, select an appropriate accredited service provider and coordinate implementation. Our approach brings together accounting knowledge, UAE VAT expertise and technology support to make the transition manageable for your team.
        </p>
        <BookButton testId="einv-hero-book-btn" />
      </div>
    </div>
  </section>
);

const WhatIs = () => (
  <section className="py-20 px-4 bg-white" data-testid="einv-what-is">
    <div className="container mx-auto max-w-5xl grid lg:grid-cols-5 gap-12">
      <div className="lg:col-span-3">
        <Eyebrow>The Basics</Eyebrow>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">What Is UAE E-Invoicing?</h2>
        <p className="text-gray-600 text-lg leading-relaxed mb-4">
          An e-invoice is structured invoice data issued and exchanged electronically between a supplier and buyer, with relevant tax data reported electronically to the Federal Tax Authority (FTA).
        </p>
        <p className="text-gray-600 text-lg leading-relaxed">
          The UAE framework uses a Decentralized Continuous Transaction Control and Exchange (DCTCE) model supported by Ministry of Finance–accredited Service Providers (ASPs).
        </p>
      </div>
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
          <p className="text-amber-900 text-sm leading-relaxed">PDFs, Word documents, scanned copies and invoices sent by email alone do not constitute structured e-invoices under this framework.</p>
        </div>
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6 flex gap-3">
          <CheckCircle className="text-indigo-600 flex-shrink-0 mt-0.5" size={22} />
          <p className="text-indigo-900 text-sm leading-relaxed">Businesses should assess their obligations against the applicable implementation phase, transaction scope and exclusions.</p>
        </div>
      </div>
    </div>
  </section>
);

const Roles = () => (
  <section className="py-20 px-4 bg-gray-50" data-testid="einv-roles">
    <div className="container mx-auto max-w-5xl">
      <Eyebrow>Who Does What</Eyebrow>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">How WPAA Supports Your Transition</h2>
      <p className="text-gray-600 text-lg mb-3 max-w-3xl">E-invoicing involves more than connecting software. Your customer records, invoice fields, VAT classifications and internal processes must also be ready.</p>
      <p className="text-gray-600 text-lg mb-10 max-w-3xl">WPAA supports the accounting, tax and implementation process, while your appointed ASP and technology providers deliver the technical connectivity.</p>
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
        <table className="w-full min-w-[560px] text-left" data-testid="einv-roles-table">
          <thead>
            <tr className="bg-gray-900 text-white">
              <th className="px-6 py-4 font-semibold w-1/2">WPAA’s Role</th>
              <th className="px-6 py-4 font-semibold w-1/2">ASP and Technology Provider’s Role</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ROLES.map(([w, a]) => (
              <tr key={w}>
                <td className="px-6 py-4 text-gray-800"><span className="inline-flex gap-2"><CheckCircle className="text-indigo-600 flex-shrink-0 mt-0.5" size={18} />{w}</span></td>
                <td className="px-6 py-4 text-gray-600">{a}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-gray-500 mt-4">The scope and responsibilities are agreed at the start of each engagement.</p>
    </div>
  </section>
);

const Services = () => (
  <section className="py-20 px-4 bg-white" data-testid="einv-services">
    <div className="container mx-auto max-w-6xl">
      <div className="text-center mb-14">
        <Eyebrow>Our Services</Eyebrow>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Our E-Invoicing Services</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        {SERVICES.map((s, i) => (
          <div key={s.title} className="border-2 border-gray-100 hover:border-indigo-400 transition-colors rounded-2xl p-7" data-testid={`einv-service-${i + 1}`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-indigo-100 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"><s.icon className="text-indigo-600" size={24} /></div>
              <h3 className="text-xl font-bold text-gray-900">{i + 1}. {s.title}</h3>
            </div>
            <p className="text-gray-700 font-medium mb-4">{s.lead}</p>
            <ul className="space-y-2">
              {s.items.map((it) => (
                <li key={it} className="flex gap-2 text-sm text-gray-600 leading-relaxed"><CheckCircle className="text-indigo-500 flex-shrink-0 mt-0.5" size={16} />{it}</li>
              ))}
            </ul>
            {s.note && <p className="text-xs text-gray-500 mt-4 italic">{s.note}</p>}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Why = () => (
  <section className="py-20 px-4 bg-gray-50" data-testid="einv-why">
    <div className="container mx-auto max-w-6xl">
      <Eyebrow>Why WPAA</Eyebrow>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-10">Why Choose WPAA?</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {WHY.map((w) => (
          <div key={w.title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <w.icon className="text-indigo-600 mb-4" size={30} />
            <h3 className="font-bold text-gray-900 mb-2">{w.title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{w.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Faqs = () => (
  <section className="py-20 px-4 bg-white" data-testid="einv-faqs">
    <div className="container mx-auto max-w-6xl">
      <div className="text-center mb-12">
        <Eyebrow>FAQs</Eyebrow>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Frequently Asked Questions</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {FAQS.map((f, i) => (
          <div key={f.q} className="bg-gray-50 border border-gray-200 rounded-2xl p-6" data-testid={`einv-faq-${i}`}>
            <h3 className="font-bold text-gray-900 mb-2 flex gap-2"><HelpCircle className="text-indigo-600 flex-shrink-0 mt-0.5" size={18} />{f.q}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Cta = () => (
  <section className="py-20 px-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white" data-testid="einv-cta">
    <div className="container mx-auto text-center max-w-3xl">
      <h2 className="text-3xl md:text-4xl font-bold mb-6">Start Your E-Invoicing Readiness Journey</h2>
      <p className="text-lg text-indigo-100 mb-8">Understand your requirements, identify gaps and prepare a practical plan for implementation.</p>
      <BookButton testId="einv-cta-book-btn" light />
      <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 mt-10 text-indigo-100 text-sm">
        <a href="tel:+971501219341" className="flex items-center justify-center gap-2 hover:text-white" data-testid="einv-phone"><Phone size={16} /> +971 50 121 9341</a>
        <a href="mailto:info@wpaauae.com" className="flex items-center justify-center gap-2 hover:text-white" data-testid="einv-email"><Mail size={16} /> info@wpaauae.com</a>
        <span className="flex items-center justify-center gap-2"><MapPin size={16} /> Office 109, Midaffa Building, Al Mahatta, Sharjah, UAE</span>
      </div>
    </div>
  </section>
);

const EInvoicingUAE = () => {
  usePageSeo({
    title: 'E-Invoicing Compliance & Integration Services in the UAE',
    description: 'Prepare for UAE e-invoicing with WPAA: readiness assessment, invoice data and VAT review, accredited service provider (ASP) selection, ERP integration coordination and training.',
    path: '/e-invoicing-uae',
    image: '/og/e-invoicing-uae.png'
  });

  return (
    <div className="min-h-screen">
      <JsonLd id="ld-einv-service" data={serviceSchema({
        name: 'E-Invoicing Compliance & Integration Services in the UAE',
        description: 'UAE e-invoicing readiness assessment, invoice data and VAT review, ASP selection and onboarding, ERP integration coordination, training and ongoing reconciliation support.',
        path: '/e-invoicing-uae',
        serviceType: 'E-Invoicing Compliance'
      })} />
      <JsonLd id="ld-einv-breadcrumb" data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Services', path: '/services' },
        { name: 'E-Invoicing UAE', path: '/e-invoicing-uae' }
      ])} />
      <JsonLd id="ld-einv-faq" data={faqSchema(FAQS)} />
      <Hero />
      <WhatIs />
      <Roles />
      <Services />
      <Why />
      <Faqs />
      <RelatedServices services={[
        { title: 'VAT Services in UAE', description: 'VAT registration, returns, reconciliation and FTA audit support.', path: '/vat-services-uae', accent: 'blue' },
        { title: 'Accounting Services', description: 'VAT-compliant bookkeeping on Zoho Books or QuickBooks.', path: '/accounting-services-uae', accent: 'indigo' },
        { title: 'UAE E-Invoicing Guide', description: 'Phases, ASP deadlines, the five-corner model and PINT AE explained.', path: '/knowledge-academy/uae-e-invoicing-phases-pint-ae-guide', accent: 'teal' }
      ]} />
      <Cta />
    </div>
  );
};

export default EInvoicingUAE;
